/*!
 * HTML Edge Labels using edge-point-utils
 * Replaces canvas-based rendering with HTML elements for better performance and flexibility
 */

import {edgeLabelSideSign, edgePointDetails, labelSideSign, pointOnEdge} from './edge-point-utils.js';

export class HtmlEdgeLabelsRenderer {
  constructor(cy, options = {}, cyservice) {
    this.cy = cy;
    this.options = {
      getText: (edge) => edge.data('label') ?? '',
      getPos: (edge) => edge.data('linkLabelPosition') ?? 0, // [-1..1]
      getNormalOffset: (edge) => -8, // Default function
      normalOffset: -8, // Fallback for backward compatibility
      rotate: true,
      snapEpsilon: 0.08,
      filter: (edge) => edge.visible() && !edge.hidden(),
      className: 'html-edge-label',
      ...options
    };

    this.container = null;
    this.labelElements = new Map(); // edge.id() -> HTMLElement
    this._parallelEdgeTimeout = null; // Timeout for parallel edge geometry updates
    this.isDestroyed = false;
    this.isDragging = false; // Track dragging state to prevent redraw from showing labels
    this.isEditing = false; // Track edge editing state for dynamic updates

    // Shared SVG for all curved labels
    this.sharedSvg = null;
    this.svgDefs = null;

    // Performance caches
    this.textCache = new Map(); // edgeId -> cached text
    this.classCache = new Map(); // edgeId -> cached classes string
    this.dataHashCache = new Map(); // edgeId -> data hash for invalidation
    this.nodeGeometryCache = new Map(); // nodeId -> last seen bounding size signature
    this._pendingGeometryNodeIds = null; // nodes whose size may have changed this frame
    this._geometryFlushHandle = null;
    this._geometryFlushUsesTimeout = false;

    this.init();
  }

  init() {
    // Create container for labels
    const cyContainer = this.cy.container();
    if (!cyContainer) {
      console.error('Cytoscape container not found');
      return;
    }

    this.container = document.createElement('div');
    this.container.className = 'html-edge-labels-container';

    cyContainer.style.position = 'relative';
    cyContainer.appendChild(this.container);

    // Create shared SVG for all curved labels
    this.createSharedSvg();

    // Bind events
    this.bindEvents();
    // DON'T call redraw immediately - let Cytoscape finish initializing
    setTimeout(() => this.redraw(), 100);
  }

  bindEvents() {
    const redrawHandler = () => this.redraw();

    // Position-only update for pan/zoom (much faster than full redraw)
    const positionUpdateHandler = () => this.updateLabelPositions();

    // Delayed redraw handler for class/visibility changes to handle timing issues
    const delayedRedrawHandler = () => {
      // Small delay to ensure all class changes are applied
      setTimeout(() => this.redraw(), 50);
    };

    // Use position-only update for pan/zoom (no content recalculation needed)
    this.cy.on('zoom pan', positionUpdateHandler);

    // Optimized handlers for structural changes
    this.cy.on('add', 'edge', (event) => {
      // Defensive check to prevent operations after destroy
      if (this.isDestroyed || !this.labelElements) return;

      const edge = event.target;
      this.addLabelsForEdges([edge]);
      // Handle parallel edge geometry updates after Cytoscape applies curvature
      this.scheduleParallelEdgeUpdate(edge);
    });
    this.cy.on('remove', 'edge', (event) => {
      // Defensive check to prevent operations after destroy
      if (this.isDestroyed || !this.labelElements) return;

      const edge = event.target;
      const edgeId = edge.id();

      // Immediately remove label to prevent any race conditions
      this.removeLabelsForEdges([edge]);

      // Also remove any orphaned labels with the same ID (in case of duplicates)
      const allLabels = this.container.querySelectorAll(`[data-edge-id="${edgeId}"]`);
      allLabels.forEach(label => {
        if (label && label.parentNode) {
          label.parentNode.removeChild(label);
        }
      });

      // Also clean up SVG text elements for sloped labels
      if (this.sharedSvg) {
        const svgTexts = this.sharedSvg.querySelectorAll(`text[data-edge-id="${edgeId}"]`);
        svgTexts.forEach(text => {
          if (text && text.parentNode) {
            text.parentNode.removeChild(text);
          }
        });
      }

      // Update remaining parallel edges after removal
      this.scheduleParallelEdgeUpdate(edge);
    });
    // Handle data changes - only redraw labels for edges, ignore node data changes
    this.cy.on('data', 'edge', (event) => {
      // Defensive check to prevent null reference errors
      if (!this.labelElements || this.isDestroyed) return;

      const edge = event.target;

      // Invalidate cache for this edge
      this.invalidateCache(edge.id());

      // Check if edge should have a label after data change
      const text = this.options.getText(edge);
      const hasComments = edge.data('hasComments');
      const classes = this.options.getClasses ? this.options.getClasses(edge) : '';
      const hasLinks = classes.includes('has-links');
      const shouldHaveLabel = (!this.options.filter || this.options.filter(edge)) &&
        ((text && text.trim().length > 0) || hasComments || hasLinks);
      const hasLabel = this.labelElements.has(edge.id());

      if (shouldHaveLabel && !hasLabel) {
        // Need to create label
        this.addLabelsForEdges([edge]);
      } else if (!shouldHaveLabel && hasLabel) {
        // Need to remove label
        this.removeLabelsForEdges([edge]);
      } else if (shouldHaveLabel && hasLabel) {
        // Check if labelSlope changed requiring element type switch (DIV <-> SVG)
        const labelElement = this.labelElements.get(edge.id());
        const currentLabelSlope = edge.data('labelSlope') || 'straight';
        const isCurrentlyDiv = labelElement.tagName.toUpperCase() === 'DIV';
        const shouldBeDiv = currentLabelSlope === 'straight';

        if (isCurrentlyDiv !== shouldBeDiv) {
          // LabelSlope changed - need to recreate label with correct element type
          // Wrap in cy.batch() to prevent edge style recalculations during label update
          this.cy.batch(() => {
            this.removeLabelsForEdges([edge]);
            this.addLabelsForEdges([edge]);
          });
        } else {
          // Same element type - just update content
          this.updateEdgeLabelContent(edge);
        }
      }
    });

    // Listen for edge class changes - sync hidden/dimmed classes to label element
    const classHandler = (event) => {
      // Defensive check to prevent null reference errors
      if (!this.labelElements || this.isDestroyed) return;

      const edge = event.target;
      const labelElement = this.labelElements.get(edge.id());
      this.syncEdgeStateClasses(edge, labelElement);
    };

    this.cy.on('class', 'edge', classHandler);

    // MAP-1559 review: a label's position along its link is clamped to the
    // source/target node borders, so a node resize (size, shape, border weight)
    // moves the label even though nothing about the edge changed. Cytoscape
    // reports those as a `style` event on the NODE - no edge event - so without
    // this the label kept its old spot until an unrelated redraw (a selection
    // change, a drag) happened to run. Only the connected edges are touched.
    const nodeGeometryHandler = (event) => {
      if (this.isDestroyed || !this.labelElements || this.labelElements.size === 0) return;

      const node = event.target;
      if (!node || typeof node.connectedEdges !== 'function') return;
      // Isolated node: no link can be affected by its size.
      if (typeof node.degree === 'function' && node.degree(false) === 0) return;

      this.queueNodeGeometryCheck(node.id());
    };

    this.cy.on('style', 'node', nodeGeometryHandler);

    // Listen for selection events that trigger visibility changes
    this.cy.on('select unselect', 'node edge', delayedRedrawHandler);

    // Dynamic updates during edge editing operations
    const startEdgeEditingHandler = () => {
      this.isEditing = true;
      // Don't hide labels anymore, keep them visible for dynamic updates
    };
    const endEdgeEditingHandler = () => {
      this.isEditing = false;
      this.redraw(); // Final cleanup redraw
    };

    // Listen for real-time anchor drag events from edge editing library
    const anchorDragHandler = (event, edge, type, index, position) => {
      // Only redraw if we're currently editing (safety check)
      if (this.isEditing) {
        this.redraw();
      }
    };

    this.cy.on('edgeediting_dragstart', startEdgeEditingHandler);
    this.cy.on('edgeediting_moveend', endEdgeEditingHandler);
    this.cy.on('edgeediting_anchordrag', anchorDragHandler);

    // Optimized node drag handlers - hide labels based on optimization mode
    this.cy.on('grab', 'node', (event) => {
      // Get the link optimization mode from local storage
      const settings = JSON.parse(localStorage.getItem('app-settings') || '{}');
      const optimizationMode = settings.linkOptimizationMode || 'NONE'; // Default to NONE

      if (optimizationMode === 'NONE') {
        // Don't set isDragging, keep labels visible
        return;
      }

      this.isDragging = true;

      if (optimizationMode === 'ALL') {
        // Hide all labels
        this.hideLabels();
      } else if (optimizationMode === 'RELATED') {
        // Hide only labels connected to the dragged node
        const grabbedNode = event.target;
        this.hideConnectedLabels(grabbedNode);
      }
    });
    this.cy.on('drag', 'node', (event) => {
      // Labels already hidden based on mode, no computations needed during drag
    });
    this.cy.on('free', 'node', (event) => {
      // Get the link optimization mode from local storage
      const settings = JSON.parse(localStorage.getItem('app-settings') || '{}');
      const optimizationMode = settings.linkOptimizationMode || 'NONE'; // Default to NONE

      if (optimizationMode === 'NONE') {
        // Nothing was hidden, nothing to restore
        return;
      }

      this.isDragging = false;

      if (optimizationMode === 'ALL') {
        // Show all labels
        this.showLabels();
      } else if (optimizationMode === 'RELATED') {
        // Show only labels connected to the freed node
        const freedNode = event.target;
        this.showConnectedLabels(freedNode);
      }
    });

    // Window resize
    window.addEventListener('resize', redrawHandler);

    // Store handlers for cleanup
    this._redrawHandler = redrawHandler;
    this._positionUpdateHandler = positionUpdateHandler;
    this._startEdgeEditingHandler = startEdgeEditingHandler;
    this._endEdgeEditingHandler = endEdgeEditingHandler;
    this._anchorDragHandler = anchorDragHandler;
    this._classHandler = classHandler;
    this._delayedRedrawHandler = delayedRedrawHandler;
    this._nodeGeometryHandler = nodeGeometryHandler;
  }

  // Queue a node for a size check on the next frame. Deferred on purpose:
  // Cytoscape emits `style` BEFORE it marks the element's style/bounds caches
  // dirty, so reading boundingBox() inside the handler would return the old
  // size. Coalescing also collapses a burst (cy.style().update() re-emits for
  // every element) into a single pass.
  queueNodeGeometryCheck(nodeId) {
    if (!nodeId) return;

    if (!this._pendingGeometryNodeIds) {
      this._pendingGeometryNodeIds = new Set();
    }
    this._pendingGeometryNodeIds.add(nodeId);

    if (this._geometryFlushHandle !== null) return;

    const run = () => {
      this._geometryFlushHandle = null;
      this._geometryFlushUsesTimeout = false;
      this.flushNodeGeometryChecks();
    };

    if (typeof requestAnimationFrame === 'function') {
      this._geometryFlushHandle = requestAnimationFrame(run);
      this._geometryFlushUsesTimeout = false;
    } else {
      this._geometryFlushHandle = setTimeout(run, 16);
      this._geometryFlushUsesTimeout = true;
    }
  }

  // Reposition the labels of every edge touching a node whose rendered size
  // actually changed (colour/opacity/class-only style events are filtered out
  // here so they never cost a reposition).
  flushNodeGeometryChecks() {
    const pending = this._pendingGeometryNodeIds;
    if (!pending || pending.size === 0) return;

    if (this.isDestroyed || !this.labelElements) {
      pending.clear();
      return;
    }

    const edgesToUpdate = [];
    const seenEdgeIds = new Set();

    pending.forEach(nodeId => {
      const node = this.cy.getElementById(nodeId);
      if (!node || !node.length || (typeof node.isNode === 'function' && !node.isNode())) {
        this.nodeGeometryCache.delete(nodeId);
        return;
      }

      const signature = this.getNodeGeometrySignature(node);
      if (signature === null || this.nodeGeometryCache.get(nodeId) === signature) return;

      this.nodeGeometryCache.set(nodeId, signature);

      node.connectedEdges().forEach(edge => {
        const edgeId = edge.id();
        if (seenEdgeIds.has(edgeId)) return;
        if (!this.labelElements.has(edgeId)) return;
        seenEdgeIds.add(edgeId);
        edgesToUpdate.push(edge);
      });
    });

    pending.clear();

    if (edgesToUpdate.length > 0) {
      this.updateLabelPositions(edgesToUpdate);
    }
  }

  // Rendered size of a node, rounded so sub-pixel noise doesn't count as a
  // change. Deliberately the SAME measure the label clamp feeds on
  // (getNodeBoundingRadius, boundingBox without labels), so anything that moves
  // the clamp - size, shape, border weight, a container fitting its children -
  // moves this signature too, and nothing else does.
  getNodeGeometrySignature(node) {
    if (typeof node.boundingBox !== 'function') return null;
    const bb = node.boundingBox({ includeLabels: false });
    if (!bb) return null;
    return `${Math.round((bb.x2 - bb.x1) * 100)}x${Math.round((bb.y2 - bb.y1) * 100)}`;
  }

  cancelPendingGeometryFlush() {
    if (this._geometryFlushHandle === null) return;

    if (this._geometryFlushUsesTimeout) {
      clearTimeout(this._geometryFlushHandle);
    } else if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this._geometryFlushHandle);
    }

    this._geometryFlushHandle = null;
    this._geometryFlushUsesTimeout = false;
  }

  createSharedSvg() {
    if (!this.container) return;

    // Create single SVG for all curved labels
    this.sharedSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.sharedSvg.setAttribute('class', 'curved-labels-svg');
    this.sharedSvg.style.cssText = `
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: visible;
      z-index: 1000;
    `;

    // Create defs section for path definitions
    this.svgDefs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    this.sharedSvg.appendChild(this.svgDefs);

    // Add to container
    this.container.appendChild(this.sharedSvg);
  }

  redraw() {
    if (this.isDestroyed || !this.container) return;

    // Skip expensive full redraw during node dragging - position updates handle it
    if (this.isDragging) {
      return;
    }
    // Clear existing labels
    this.clearLabels();

    // Get visible edges that need labels
    const allEdges = this.cy.edges();
    const edges = allEdges.filter(edge => {
      if (!this.options.filter(edge)) return false;
      const text = this.options.getText(edge);
      const hasComments = edge.data('hasComments');
      const classes = this.options.getClasses ? this.options.getClasses(edge) : '';
      const hasLinks = classes.includes('has-links');
      // Include edges that have text, comments, OR link icons
      return (text && text.trim().length > 0) || hasComments || hasLinks;
    });

    // Create labels for each edge
    edges.forEach(edge => {
      this.createLabelForEdge(edge);
    });

    // Ensure labels are visible after redraw
    this.showLabels();
  }

  // Cache management
  getDataHash(edge) {
    const data = edge.data();
    // Create simple hash from relevant properties that affect label content/classes
    return JSON.stringify({
      label: data.label,
      name: data.name,
      metadata: data.metadata,
      hasComments: data.hasComments,
      excludedFromState: data.excludedFromState
    });
  }

  getCachedText(edge) {
    const edgeId = edge.id();
    const currentHash = this.getDataHash(edge);
    const cachedHash = this.dataHashCache.get(edgeId);

    // Check if cache is valid
    if (cachedHash === currentHash && this.textCache.has(edgeId)) {
      return this.textCache.get(edgeId);
    }

    // Cache miss - compute and store
    const text = this.options.getText(edge);
    this.textCache.set(edgeId, text);
    this.dataHashCache.set(edgeId, currentHash);
    return text;
  }

  getCachedClasses(edge) {
    // Don't cache classes - they depend on external state (edit mode, map state)
    // that isn't part of the edge data and can change without edge data changing
    return this.options.getClasses ? this.options.getClasses(edge) : this.options.className;
  }

  invalidateCache(edgeId) {
    this.textCache.delete(edgeId);
    this.classCache.delete(edgeId);
    this.dataHashCache.delete(edgeId);
  }

  // Clear all class caches - needed when external state (like edit mode) changes
  // that affects class computation but doesn't change edge data
  clearClassCache() {
    this.classCache.clear();
    this.dataHashCache.clear();
  }

  // Fast position-only update for pan/zoom - no content recalculation
  updateLabelPositions(edgeCollection) {
    if (this.isDestroyed || !this.container || !this.labelElements) return;

    const edgesToUpdate = [];

    if (edgeCollection && typeof edgeCollection.forEach === 'function') {
      edgeCollection.forEach(edge => edgesToUpdate.push(edge));
    } else {
      this.labelElements.forEach((_labelElement, edgeId) => {
        const edge = this.cy.getElementById(edgeId);
        if (!edge.length) return;
        edgesToUpdate.push(edge);
      });
    }

    this.updateLabelsForEdges(edgesToUpdate);
  }

  updateLabelsForEdges(edgeCollection) {
    if (this.isDestroyed || !this.container || !edgeCollection) return;

    const edges = [];

    if (Array.isArray(edgeCollection)) {
      edges.push(...edgeCollection);
    } else if (typeof edgeCollection.forEach === 'function') {
      edgeCollection.forEach(edge => edges.push(edge));
    } else {
      edges.push(edgeCollection);
    }

    // Cache zoom/pan values once per update cycle to avoid repeated calculations
    const cachedZoom = this.cy.zoom();
    const cachedPan = this.cy.pan();

    edges.forEach(edge => {
      if (!edge || typeof edge.id !== 'function' || (typeof edge.isEdge === 'function' && !edge.isEdge())) return;
      const labelElement = this.labelElements?.get(edge.id());
      if (!labelElement) return;
      this.updateSingleLabelPosition(edge, labelElement, cachedZoom, cachedPan);
    });
  }

  // Update position of a single label element
  updateSingleLabelPosition(edge, labelElement, cachedZoom, cachedPan) {
    const labelSlope = edge.data('labelSlope') || 'straight';

    if (labelSlope === 'sloped') {
      // Handle curved/sloped labels in shared SVG
      this.updateCurvedLabelPosition(edge, labelElement, cachedZoom, cachedPan);
      return;
    }

    // Fast position update for standard labels
    let s = +this.options.getPos(edge) || 0;

    if (this.options.snapEpsilon > 0) {
      if (Math.abs(s + 1) < this.options.snapEpsilon) s = -1;
      else if (Math.abs(s) < this.options.snapEpsilon) s = 0;
      else if (Math.abs(s - 1) < this.options.snapEpsilon) s = 1;
    }

    const t = Math.max(0, Math.min(1, (s + 1) / 2));
    const edgeDetails = edgePointDetails(edge, t);
    if (!edgeDetails) return;

    const { point, tangent, normal } = edgeDetails;

    // Use cached zoom and pan values to avoid repeated calculations
    const zoom = cachedZoom !== undefined ? cachedZoom : this.cy.zoom();
    const pan = cachedPan !== undefined ? cachedPan : this.cy.pan();
    let angle = 0;

    if (this.options.rotate && tangent) {
      const labelSlope = edge.data('labelSlope') || 'straight';
      if (labelSlope === 'sloped') {
        angle = Math.atan2(tangent.y, tangent.x);
        // Normalize angle
        while (angle > Math.PI) angle -= 2 * Math.PI;
        while (angle < -Math.PI) angle += 2 * Math.PI;
        // Keep text readable
        if (angle > Math.PI / 2) {
          angle -= Math.PI;
        } else if (angle < -Math.PI / 2) {
          angle += Math.PI;
        }
      }
    }

    // Convert edge point to screen coordinates
    const screenX = point.x * zoom + pan.x;
    const screenY = point.y * zoom + pan.y;

    // Get dynamic normal offset and scale with zoom
    const normalOffset = typeof this.options.getNormalOffset === 'function'
      ? this.options.getNormalOffset(edge)
      : this.options.normalOffset;
    const normalOffsetDistance = normalOffset * zoom;
    let offsetX = screenX;
    let offsetY = screenY;

    if (normal && normalOffsetDistance !== 0) {
      const side = edgeLabelSideSign(edge);
      offsetX += normal.x * normalOffsetDistance * side;
      offsetY += normal.y * normalOffsetDistance * side;
    }

    // Update font size for zoom
    let scaledFontSize = 12;
    const renderedFontSize = edge.data('textFontSize');
    if (renderedFontSize) {
      const numericSize = parseFloat(renderedFontSize);
      if (!isNaN(numericSize)) {
        scaledFontSize = numericSize * zoom;
      }
    } else {
      const baseFontSize = parseInt(this.getEdgeStyle(edge, 'font-size') || '12', 10);
      scaledFontSize = baseFontSize * zoom;
    }

    // Use hardware-accelerated transforms for better performance
    // Position using transform3d instead of left/top for GPU acceleration
    labelElement.style.left = '0px';
    labelElement.style.top = '0px';
    labelElement.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0) translate(-50%, -50%) rotate(${angle}rad)`;
    labelElement.style.fontSize = `${scaledFontSize}px`;
  }

  createLabelForEdge(edge) {
    // Use cached text to avoid expensive getText() calls
    const text = this.getCachedText(edge) || '';
    const hasComments = edge.data('hasComments');
    const classes = this.options.getClasses ? this.options.getClasses(edge) : '';
    const hasLinks = classes.includes('has-links');

    // Skip if no text and no icons to show
    if (!text.trim() && !hasComments && !hasLinks) return;

    const labelSlope = edge.data('labelSlope') || 'straight';
    if (labelSlope === 'sloped') {
      this.createCurvedLabelForEdge(edge, text);
    } else {
      this.createStandardLabelForEdge(edge, text);
    }
  }

  createStandardLabelForEdge(edge, text) {
    let s = +this.options.getPos(edge) || 0;

    if (this.options.snapEpsilon > 0) {
      if (Math.abs(s + 1) < this.options.snapEpsilon) s = -1;
      else if (Math.abs(s) < this.options.snapEpsilon) s = 0;
      else if (Math.abs(s - 1) < this.options.snapEpsilon) s = 1;
    }

    const t = Math.max(0, Math.min(1, (s + 1) / 2));
    const edgeDetails = edgePointDetails(edge, t);
    if (!edgeDetails) return;

    const { point, tangent, normal } = edgeDetails;

    // Get zoom and pan for coordinate conversion
    const zoom = this.cy.zoom();
    const pan = this.cy.pan();
    let angle = 0;
    if (this.options.rotate && tangent) {
      // Check labelSlope data to determine rotation behavior
      const labelSlope = edge.data('labelSlope') || 'straight';
      if (labelSlope === 'sloped') {
        angle = Math.atan2(tangent.y, tangent.x);

        // Normalize angle to [-π, π] range
        while (angle > Math.PI) angle -= 2 * Math.PI;
        while (angle < -Math.PI) angle += 2 * Math.PI;

        // Keep text readable by ensuring it's never more than 90 degrees from horizontal
        // If angle is in the range where text would be upside down, flip it
        if (angle > Math.PI / 2) {
          angle -= Math.PI;
        } else if (angle < -Math.PI / 2) {
          angle += Math.PI;
        }
      }
      // For 'straight' labels, angle remains 0 (horizontal)
    }

    // Convert edge point to screen coordinates first
    const screenX = point.x * zoom + pan.x;
    const screenY = point.y * zoom + pan.y;

    // Get dynamic normal offset and scale with zoom
    const normalOffset = typeof this.options.getNormalOffset === 'function'
      ? this.options.getNormalOffset(edge)
      : this.options.normalOffset;
    const normalOffsetDistance = normalOffset * zoom;
    let offsetX = screenX;
    let offsetY = screenY;

    if (normal && normalOffsetDistance !== 0) {
      const side = edgeLabelSideSign(edge);
      offsetX += normal.x * normalOffsetDistance * side;
      offsetY += normal.y * normalOffsetDistance * side;
    }

    // Create label element
    const labelElement = document.createElement('div');
    // Get classes with icon indicators from cytoscape service (cached)
    let classes = this.getCachedClasses(edge);
    // Add icon-only class when there's no text (for CSS styling)
    if (!text || !text.trim()) {
      classes += ' icon-only';
    }
    labelElement.className = classes;
    labelElement.textContent = text;

    // Add hover event listeners
    if (this.options.onHover) {
      labelElement.addEventListener('mouseenter', (event) => {
        this.options.onHover(edge, event);
      });
    }

    if (this.options.onHoverOut) {
      labelElement.addEventListener('mouseleave', (event) => {
        this.options.onHoverOut(edge, event);
      });
    }

    // Get the actual rendered font size from Cytoscape's built-in labels
    let scaledFontSize = 12;
    const renderedFontSize = edge.data('textFontSize');
    if (renderedFontSize) {
      // Parse units from textFontSize (e.g., "14px" -> 14) and apply direct zoom scaling like node labels
      const numericSize = parseFloat(renderedFontSize);
      if (!isNaN(numericSize)) {
        scaledFontSize = numericSize * zoom;
      }
    } else {
      // Fallback to manual scaling if no textFontSize data
      const baseFontSize = parseInt(this.getEdgeStyle(edge, 'font-size') || '12', 10);
      scaledFontSize = baseFontSize * zoom;
    }

    // Apply positioning styles only - visual styles are in SCSS
    labelElement.style.cssText = `
      left: ${offsetX}px;
      top: ${offsetY}px;
      transform: translate(-50%, -50%) rotate(${angle}rad);
      font-size: ${scaledFontSize}px;
      pointer-events: ${this.options.onHover || this.options.onHoverOut ? 'auto' : 'none'};
    `;

    // Mirror the edge's link-view-mode state (hidden / MAP-1017 dimmed)
    this.syncEdgeStateClasses(edge, labelElement);

    this.container.appendChild(labelElement);
    this.labelElements.set(edge.id(), labelElement);
    document.documentElement.style.setProperty('--html-edge-label-font-size', ` ${scaledFontSize}px`);
  }

  createCurvedLabelForEdge(edge, text) {
    if (!this.sharedSvg || !this.svgDefs) return;

    let s = +this.options.getPos(edge) || 0;
    if (this.options.snapEpsilon > 0) {
      if (Math.abs(s + 1) < this.options.snapEpsilon) s = -1;
      else if (Math.abs(s) < this.options.snapEpsilon) s = 0;
      else if (Math.abs(s - 1) < this.options.snapEpsilon) s = 1;
    }

    const zoom = this.cy.zoom();
    const pan = this.cy.pan();
    let scaledFontSize = 12;
    const renderedFontSize = edge.data('textFontSize');
    if (renderedFontSize) {
      const n = parseFloat(renderedFontSize);
      if (!isNaN(n)) scaledFontSize = n * zoom;
    } else {
      const base = parseInt(this.getEdgeStyle(edge, 'font-size') || '12', 10);
      scaledFontSize = base * zoom;
    }

    const baseClasses = this.getCachedClasses(edge);
    const hasLinks = baseClasses.includes('has-links');
    const hasComments = baseClasses.includes('has-comments');
    const normalOffset = typeof this.options.getNormalOffset === 'function'
      ? this.options.getNormalOffset(edge)
      : this.options.normalOffset;
    const offsetForPath = normalOffset * zoom;

    // Generate path and add to shared SVG defs
    const { d: pathData, reversed: pathReversed } = this.generateSVGPathFromEdge(edge, zoom, pan, offsetForPath);
    const pathId = `edge-path-${edge.id()}`;

    // Create path element in defs
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('id', pathId);
    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'none');

    const isDebugMode = localStorage.getItem('debug') === 'true';
    if (isDebugMode) {
      path.setAttribute('stroke', 'red');
      path.setAttribute('stroke-width', '2');
    } else {
      path.setAttribute('stroke', 'none');
    }

    // Add path to defs
    this.svgDefs.appendChild(path);

    // Create text element in shared SVG
    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textElement.setAttribute('font-size', scaledFontSize);
    textElement.setAttribute('font-family', 'inherit');
    textElement.setAttribute('dominant-baseline', 'middle');
    textElement.setAttribute('data-edge-id', edge.id()); // For cleanup reference

    const curvedClasses = baseClasses.replace(this.options.className, this.options.className + '-curved');
    textElement.setAttribute('class', curvedClasses);

    const outlineWidth = Math.max(1, Math.round(scaledFontSize * 0.1));
    textElement.setAttribute('style', [
      'fill: var(--smap-background-color);',
      `stroke: var(--smap-background-color, rgba(255,255,255,0.95));`,
      `stroke-width: ${outlineWidth}px;`,
      'stroke-linejoin: round;',
      'stroke-linecap: round;',
      'paint-order: stroke fill;',
    ].join(' '));

    // Create textPath referencing the path in defs
    const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
    textPath.setAttribute('href', `#${pathId}`);
    const centerT = this.curvedLabelStartOffsetFraction(s, pathReversed);
    const startOffset = `${centerT * 100}%`;
    textPath.setAttribute('startOffset', startOffset);
    textPath.setAttribute('text-anchor', 'middle');

    // Add icons and text to textPath
    if (hasLinks) {
      const linkIcon = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      linkIcon.setAttribute('font-family', 'maporium');
      linkIcon.setAttribute('font-size', scaledFontSize);
      linkIcon.setAttribute('class', 'edge-label-icon edge-label-icon-link');
      linkIcon.textContent = '\ue03a';
      linkIcon.setAttribute('baseline-shift', '-2px');
      linkIcon.setAttribute('style', 'fill: var(--html-edge-label-color);');
      textPath.appendChild(linkIcon);
    }

    const textSpan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
    textSpan.setAttribute('font-family', 'inherit');
    textSpan.setAttribute('font-size', scaledFontSize);
    textSpan.setAttribute('class', 'edge-label-text');
    textSpan.setAttribute('style', 'fill: var(--html-edge-label-color);');
    textSpan.setAttribute('dx', '4');
    textSpan.textContent = text;
    textPath.appendChild(textSpan);

    if (hasComments) {
      const commentIcon = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      commentIcon.setAttribute('font-family', 'maporium');
      commentIcon.setAttribute('font-size', scaledFontSize);
      commentIcon.setAttribute('class', 'edge-label-icon edge-label-icon-comment');
      commentIcon.textContent = ' \ue039';
      commentIcon.setAttribute('baseline-shift', '2px');
      commentIcon.setAttribute('style', 'fill: var(--html-edge-label-color);');
      textPath.appendChild(commentIcon);
    }

    // Assemble text element
    textElement.appendChild(textPath);

    // Mirror the edge's link-view-mode state (hidden / MAP-1017 dimmed)
    this.syncEdgeStateClasses(edge, textElement);

    // Add to shared SVG
    this.sharedSvg.appendChild(textElement);

    // Store reference to text element (not individual SVG)
    this.labelElements.set(edge.id(), textElement);
    document.documentElement.style.setProperty('--html-edge-label-font-size', ` ${scaledFontSize}px`);
  }



  /**
   * MAP-1559 review: a sloped label rides an SVG `textPath`, and `startOffset`
   * is a fraction of THAT path, not of the source-to-target direction. The path
   * below is emitted target-to-source whenever the link runs right-to-left, so
   * the glyphs stay upright instead of reading upside down. An alignment
   * measured from the source therefore has to be mirrored when the path was
   * reversed, otherwise the same fraction is counted from the wrong end.
   *
   * With a symmetric alignment (the stored default 0 renders as the exact
   * midpoint) the mirror is a no-op, which is why this stayed invisible until
   * the border clamp started producing an asymmetric window for nodes of very
   * different sizes.
   */
  curvedLabelStartOffsetFraction(s, reversed) {
    const t = Math.max(0, Math.min(1, (s + 1) / 2));
    return reversed ? 1 - t : t;
  }

  /**
   * Builds the `textPath` geometry for a sloped label. Returns the path data
   * plus whether it was emitted target-to-source, which the caller needs to
   * mirror `startOffset` (see `curvedLabelStartOffsetFraction`).
   */
  generateSVGPathFromEdge(edge, zoom, pan, normalOffsetPx) {
    const getAllPts = () => {
      const rs = edge && edge._private && edge._private.rscratch;
      return rs && Array.isArray(rs.allpts) ? rs.allpts : null;
    };

    const toScreen = (p) => ({ x: p.x * zoom + pan.x, y: p.y * zoom + pan.y });

    const buildPolylineFromRenderer = () => {
      const a = getAllPts();
      if (!a || a.length < 4) return null;
      const pts = [];
      for (let i = 0; i < a.length; i += 2) pts.push({ x: a[i], y: a[i + 1] });
      return pts.map(toScreen);
    };

    const buildPolylineFromData = () => {
      const src = edge.source().position();
      const bends = edge.data('bendPointPositions') || [];
      const tgt = edge.target().position();
      const pts = [src, ...bends, tgt].map(toScreen);
      return pts;
    };

    const isSegmented =
      edge.data('type') === 'STRAIGHT' &&
      ((edge.data('bendPointPositions') && edge.data('bendPointPositions').length > 0) ||
        (getAllPts() && getAllPts().length > 4));

    if (isSegmented) {
      let pts = buildPolylineFromRenderer() || buildPolylineFromData();
      if (!pts || pts.length < 2) return { d: '', reversed: false };
      const shouldReverse = labelSideSign(pts[0], pts[pts.length - 1]) < 0;
      if (shouldReverse) pts = pts.slice().reverse();

      // The normals below are taken from `pts` AFTER the reversal, so they are
      // already in a left-to-right frame. Signing the offset here as well would
      // undo that and drop the label under the link.
      const offset = normalOffsetPx;
      if (offset) {
        const segN = [];
        for (let i = 0; i < pts.length - 1; i++) {
          const dx = pts[i + 1].x - pts[i].x;
          const dy = pts[i + 1].y - pts[i].y;
          const L = Math.hypot(dx, dy) || 1;
          segN[i] = { x: -dy / L, y: dx / L };
        }
        const out = [];
        for (let i = 0; i < pts.length; i++) {
          let nx, ny;
          if (i === 0) {
            nx = segN[0].x; ny = segN[0].y;
          } else if (i === pts.length - 1) {
            nx = segN[segN.length - 1].x; ny = segN[segN.length - 1].y;
          } else {
            nx = segN[i - 1].x + segN[i].x;
            ny = segN[i - 1].y + segN[i].y;
            const len = Math.hypot(nx, ny);
            if (len > 1e-6) { nx /= len; ny /= len; } else { nx = segN[i - 1].x; ny = segN[i - 1].y; }
          }
          out[i] = { x: pts[i].x + nx * offset, y: pts[i].y + ny * offset };
        }
        pts = out;
      }
      let d = '';
      for (let i = 0; i < pts.length; i++) d += (i === 0 ? `M ${pts[i].x} ${pts[i].y}` : ` L ${pts[i].x} ${pts[i].y}`);
      return { d, reversed: shouldReverse };
    }

    const samples = 100;
    const points = [];
    const firstPoint = edgePointDetails(edge, 0);
    const lastPoint = edgePointDetails(edge, 1);
    const side = labelSideSign(firstPoint && firstPoint.point, lastPoint && lastPoint.point);
    const shouldReverse = side < 0;
    const adjustedNormalOffset = normalOffsetPx * side;

    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const details = edgePointDetails(edge, t);
      if (!details || !details.point) continue;
      const { point, normal } = details;
      let x = point.x * zoom + pan.x;
      let y = point.y * zoom + pan.y;
      if (normal && adjustedNormalOffset !== 0 && Number.isFinite(normal.x) && Number.isFinite(normal.y)) {
        x += normal.x * adjustedNormalOffset;
        y += normal.y * adjustedNormalOffset;
      }
      if (Number.isFinite(x) && Number.isFinite(y)) points.push({ x, y });
    }

    if (points.length < 2) return { d: '', reversed: false };
    if (shouldReverse) points.reverse();
    let pathData = '';
    for (let i = 0; i < points.length; i++) {
      pathData += (i === 0 ? `M ${points[i].x} ${points[i].y}` : ` L ${points[i].x} ${points[i].y}`);
    }
    return { d: pathData, reversed: !!shouldReverse };
  }




  // Enhanced path smoothing for better handling of control points and curves
  smoothPath(points) {
    if (points.length < 3) return points;

    // Calculate adaptive threshold based on overall path characteristics
    const totalLength = this.calculatePathLength(points);
    const avgSegmentLength = totalLength / (points.length - 1);
    const jumpThreshold = avgSegmentLength * 2.5; // More adaptive threshold

    const smoothed = [points[0]]; // Keep first point

    for (let i = 1; i < points.length - 1; i++) {
      const prev = smoothed[smoothed.length - 1]; // Use last smoothed point
      const curr = points[i];
      const next = points[i + 1];

      // Calculate distances
      const distPrev = Math.hypot(curr.x - prev.x, curr.y - prev.y);
      const distNext = Math.hypot(next.x - curr.x, next.y - curr.y);

      // Detect sharp turns or discontinuities
      const isSharpTurn = this.detectSharpTurn(prev, curr, next, jumpThreshold);
      const isSuddenJump = distPrev > jumpThreshold;

      if (isSuddenJump || isSharpTurn) {
        // Use more sophisticated interpolation for problematic points
        if (i > 1 && i < points.length - 2) {
          // Catmull-Rom spline interpolation for smoother curves
          const p0 = points[i - 2];
          const p1 = prev;
          const p2 = next;
          const p3 = points[i + 2];

          const interpolated = this.catmullRomInterpolation(p0, p1, p2, p3, 0.5);
          smoothed.push(interpolated);
        } else {
          // Fallback to simple interpolation at path edges
          const interpolated = {
            x: (prev.x + next.x) / 2,
            y: (prev.y + next.y) / 2
          };
          smoothed.push(interpolated);
        }
      } else {
        smoothed.push(curr);
      }
    }

    smoothed.push(points[points.length - 1]); // Keep last point
    return smoothed;
  }

  // Calculate total path length for adaptive thresholding
  calculatePathLength(points) {
    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
      totalLength += Math.hypot(
        points[i].x - points[i-1].x,
        points[i].y - points[i-1].y
      );
    }
    return totalLength;
  }

  // Detect sharp turns that might cause label artifacts
  detectSharpTurn(prev, curr, next, threshold) {
    // Calculate angle between vectors
    const vec1 = { x: curr.x - prev.x, y: curr.y - prev.y };
    const vec2 = { x: next.x - curr.x, y: next.y - curr.y };

    const len1 = Math.hypot(vec1.x, vec1.y);
    const len2 = Math.hypot(vec2.x, vec2.y);

    if (len1 === 0 || len2 === 0) return false;

    // Normalize vectors
    vec1.x /= len1; vec1.y /= len1;
    vec2.x /= len2; vec2.y /= len2;

    // Calculate dot product (cosine of angle)
    const dotProduct = vec1.x * vec2.x + vec1.y * vec2.y;

    // Sharp turn if angle > 90 degrees (dot product < 0)
    return dotProduct < -0.1;
  }

  // Catmull-Rom spline interpolation for smoother curves
  catmullRomInterpolation(p0, p1, p2, p3, t) {
    const t2 = t * t;
    const t3 = t2 * t;

    return {
      x: 0.5 * (
        2 * p1.x +
        (-p0.x + p2.x) * t +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
      ),
      y: 0.5 * (
        2 * p1.y +
        (-p0.y + p2.y) * t +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
      )
    };
  }

  /**
   * Mirrors the edge's link-view-mode state onto its label element: `hidden`
   * takes the label out with the edge, `dimmed` (MAP-1017) fades it to the
   * same filtering opacity the edge line renders at. Applied at creation, on
   * every cytoscape `class` event, and after a class rebuild so neither flag
   * is lost.
   */
  syncEdgeStateClasses(edge, labelElement) {
    if (!labelElement || !edge || typeof edge.hasClass !== 'function') return;
    labelElement.classList.toggle('edge-hidden', edge.hasClass('hidden'));
    // `.dimmed.highlighted` renders the edge at full opacity (cy-styles.ts), so
    // the label follows it back to full opacity too.
    labelElement.classList.toggle(
      'edge-dimmed',
      edge.hasClass('dimmed') && !edge.hasClass('highlighted')
    );
    labelElement.classList.toggle(
      'edge-unlabeled',
      edge.hasClass('unlabeled') && !edge.hasClass('highlighted')
    );
  }

  getEdgeStyle(edge, property) {
    try {
      return edge.style(property);
    } catch (e) {
      return null;
    }
  }

  hideLabels() {
    // Hide all label elements without removing them
    if (this.container) {
      this.container.style.display = 'none';
    }
  }

  showLabels() {
    // Show all label elements only if not currently dragging
    if (this.container && !this.isDragging) {
      this.container.style.display = 'block';
    }
  }

  clearLabels() {
    // Defensive check to prevent null reference errors
    if (!this.labelElements) return;

    // Remove all label elements from DOM
    this.labelElements.forEach((element, edgeId) => {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.labelElements.clear();

    // Clear shared SVG contents (but keep the SVG container)
    if (this.svgDefs) {
      // Clear all path definitions
      while (this.svgDefs.firstChild) {
        this.svgDefs.removeChild(this.svgDefs.firstChild);
      }
    }

    if (this.sharedSvg) {
      // Remove all text elements (keep defs)
      const textElements = this.sharedSvg.querySelectorAll('text');
      textElements.forEach(textEl => textEl.remove());
    }
  }

  // Handle parallel edge geometry updates when edges are added/removed
  scheduleParallelEdgeUpdate(referenceEdge) {
    // Store IDs immediately, as the edge might be deleted
    const source = referenceEdge.source();
    const target = referenceEdge.target();

    if (!source || !target || !source.id || !target.id) return;

    // Store the IDs before any async operations
    const sourceId = source.id();
    const targetId = target.id();
    const referenceEdgeId = referenceEdge.id();

    // Clear any pending update
    if (this._parallelEdgeTimeout) {
      clearTimeout(this._parallelEdgeTimeout);
    }

    // Schedule update after Cytoscape applies automatic bending
    // 50ms to ensure geometry recalculation completes
    this._parallelEdgeTimeout = setTimeout(() => {
      // Find all parallel edges using the stored IDs (not the potentially deleted edge)
      let parallelEdges = this.cy.edges().filter(e => {
        if (!e || !e.id || !e.isEdge || !e.isEdge()) return false;

        // Skip the reference edge if it still exists
        if (e.id() === referenceEdgeId) return false;

        // Check if this edge still has valid source and target
        const edgeSource = e.source();
        const edgeTarget = e.target();
        if (!edgeSource || !edgeTarget) return false;

        const isSameDirection =
          edgeSource.id() === sourceId && edgeTarget.id() === targetId;
        const isOppositeDirection =
          edgeSource.id() === targetId && edgeTarget.id() === sourceId;
        return isSameDirection || isOppositeDirection;
      });

      // Only update if there are parallel edges (Cytoscape applies curvature)
      if (parallelEdges.length > 0) {
        // CRITICAL: Sort edges by ID to ensure stable ordering
        // This prevents label swapping when Cytoscape reorders internal geometry
        parallelEdges = parallelEdges.sort((a, b) => {
          const idA = a.id();
          const idB = b.id();
          return idA < idB ? -1 : idA > idB ? 1 : 0;
        });

        // Recreate labels for parallel edges to ensure correct matching
        parallelEdges.forEach(edge => {
          this.removeLabelsForEdges([edge]);
        });
        parallelEdges.forEach(edge => {
          this.addLabelsForEdges([edge]);
        });
      }
      this._parallelEdgeTimeout = null;
    }, 50);
  }

  // Add labels for specific edges (performance optimization for incremental loading)
  addLabelsForEdges(edges) {
    if (this.isDestroyed || !this.container) return;

    edges.forEach(edge => {
      // Verify edge still exists in graph
      if (!edge || !edge.id || !edge.isEdge || !edge.isEdge()) return;

      // Double-check the edge is still in the graph collection
      const edgeStillExists = this.cy.getElementById(edge.id());
      if (!edgeStillExists || edgeStillExists.length === 0) return;

      // Skip if label already exists
      if (this.labelElements.has(edge.id())) return;

      // Check if edge should have a label
      if (!this.options.filter || this.options.filter(edge)) {
        const text = this.options.getText(edge);
        const hasComments = edge.data('hasComments');
        const classes = this.options.getClasses ? this.options.getClasses(edge) : '';
        const hasLinks = classes.includes('has-links');
        if ((text && text.trim().length > 0) || hasComments || hasLinks) {
          this.createLabelForEdge(edge);
        }
      }
    });

    // Ensure labels are visible after creation
    this.showLabels();
  }

  // Remove labels for specific edges (performance optimization)
  removeLabelsForEdges(edges) {
    // Defensive check to prevent null reference errors
    if (!this.labelElements || this.isDestroyed) return;

    edges.forEach(edge => {
      const edgeId = edge.id();
      const labelElement = this.labelElements.get(edgeId);

      if (labelElement) {
        // Remove from DOM
        if (labelElement.parentNode) {
          labelElement.parentNode.removeChild(labelElement);
        }

        // Remove from our tracking
        this.labelElements.delete(edgeId);

        // Remove associated path from shared SVG defs
        if (this.svgDefs) {
          const pathId = `edge-path-${edgeId}`;
          const pathElement = this.svgDefs.querySelector(`#${pathId}`);
          if (pathElement) {
            pathElement.remove();
          }
        }

        // Clear caches
        this.invalidateCache(edgeId);
      }
    });
  }

  // Update content of an existing edge label without rebuilding from scratch
  updateEdgeLabelContent(edge) {
    const labelElement = this.labelElements.get(edge.id());
    if (!labelElement) return;

    const newText = this.options.getText(edge);
    const labelSlope = edge.data('labelSlope') || 'straight';

    if (labelSlope === 'sloped') {
      // For curved labels in SVG, update the text content in textPath
      const textPath = labelElement.querySelector('textPath');
      if (textPath) {
        const textSpan = textPath.querySelector('.edge-label-text');
        if (textSpan) {
          textSpan.textContent = newText;
        }
      }
    } else {
      // For standard DIV labels, update text content directly
      const textSpan = labelElement.querySelector('.edge-label-text');
      if (textSpan) {
        textSpan.textContent = newText;
      }
    }

    // Update classes if needed
    const baseClasses = this.getCachedClasses(edge);
    if (labelSlope === 'sloped') {
      // SVG elements use setAttribute
      labelElement.setAttribute('class', baseClasses);
    } else {
      // DIV elements use className
      labelElement.className = baseClasses;
    }
    // The rebuild above drops the link-view-mode classes - re-apply them.
    this.syncEdgeStateClasses(edge, labelElement);

    // Update position immediately if font size, position, or offset changed
    // This provides immediate visual feedback without waiting for form debounce
    const zoom = this.cy.zoom();
    const pan = this.cy.pan();
    this.updateSingleLabelPosition(edge, labelElement, zoom, pan);
  }

  // Update position of a curved label in shared SVG
  updateCurvedLabelPosition(edge, textElement, cachedZoom, cachedPan) {
    if (!this.sharedSvg || !this.svgDefs || !textElement) return;

    const pathId = `edge-path-${edge.id()}`;
    const existingPath = this.svgDefs.querySelector(`#${pathId}`);
    if (!existingPath) return;

    // Use cached zoom/pan values to avoid repeated calculations
    const zoom = cachedZoom !== undefined ? cachedZoom : this.cy.zoom();
    const pan = cachedPan !== undefined ? cachedPan : this.cy.pan();

    const normalOffset = typeof this.options.getNormalOffset === 'function'
      ? this.options.getNormalOffset(edge)
      : this.options.normalOffset;
    const offsetForPath = normalOffset * zoom;

    // Update path data
    const { d: pathData, reversed: pathReversed } = this.generateSVGPathFromEdge(edge, zoom, pan, offsetForPath);
    existingPath.setAttribute('d', pathData);

    // Update font size for zoom
    let scaledFontSize = 12;
    const renderedFontSize = edge.data('textFontSize');
    if (renderedFontSize) {
      const n = parseFloat(renderedFontSize);
      if (!isNaN(n)) scaledFontSize = n * zoom;
    } else {
      const base = parseInt(this.getEdgeStyle(edge, 'font-size') || '12', 10);
      scaledFontSize = base * zoom;
    }

    // Update text element font size
    textElement.setAttribute('font-size', scaledFontSize);

    // Update all child elements' font sizes
    const tspans = textElement.querySelectorAll('tspan');
    tspans.forEach(tspan => {
      tspan.setAttribute('font-size', scaledFontSize);
    });

    // Update textPath startOffset for position along curve
    let s = +this.options.getPos(edge) || 0;
    if (this.options.snapEpsilon > 0) {
      if (Math.abs(s + 1) < this.options.snapEpsilon) s = -1;
      else if (Math.abs(s) < this.options.snapEpsilon) s = 0;
      else if (Math.abs(s - 1) < this.options.snapEpsilon) s = 1;
    }
    const centerT = this.curvedLabelStartOffsetFraction(s, pathReversed);
    const startOffset = `${centerT * 100}%`;

    const textPath = textElement.querySelector('textPath');
    if (textPath) {
      textPath.setAttribute('startOffset', startOffset);
    }

    // Update outline width for zoom
    const outlineWidth = Math.max(1, Math.round(scaledFontSize * 0.1));
    const currentStyle = textElement.getAttribute('style') || '';
    const updatedStyle = currentStyle.replace(
      /stroke-width:\s*\d+(\.\d+)?px/,
      `stroke-width: ${outlineWidth}px`
    );
    textElement.setAttribute('style', updatedStyle);
  }

  // Get label data for external use (e.g., gizmo interaction)
  getCustomLabelData(edge) {
    if (edge) {
      return this.getCustomLabelDataForEdge(edge);
    }

    // Return data for all edges
    const result = [];
    this.cy.edges().forEach(e => {
      const data = this.getCustomLabelDataForEdge(e);
      if (data) result.push(data);
    });
    return result;
  }

  getCustomLabelDataForEdge(edge) {
    if (!this.options.filter(edge)) return null;

    const text = this.options.getText(edge);
    if (!text) return null;

    const element = this.labelElements.get(edge.id());
    if (!element) return null;

    // Get position info
    let s = +this.options.getPos(edge) || 0;
    if (this.options.snapEpsilon > 0) {
      if (Math.abs(s + 1) < this.options.snapEpsilon) s = -1;
      else if (Math.abs(s) < this.options.snapEpsilon) s = 0;
      else if (Math.abs(s - 1) < this.options.snapEpsilon) s = 1;
    }
    const t = Math.max(0, Math.min(1, (s + 1) / 2));

    const edgeDetails = edgePointDetails(edge, t);
    if (!edgeDetails) return null;

    const { point } = edgeDetails;
    const zoom = this.cy.zoom();
    const pan = this.cy.pan();

    // Get actual element dimensions
    const rect = element.getBoundingClientRect();

    return {
      edgeId: edge.id(),
      text,
      x: point.x, // model coordinates
      y: point.y,
      screenX: point.x * zoom + pan.x, // screen coordinates
      screenY: point.y * zoom + pan.y,
      width: rect.width,
      height: rect.height,
      angle: 0 // TODO: extract from transform if needed
    };
  }

  // Hit test for label interaction
  hitTestLabelAtRenderedPoint(rx, ry, extraPadPx = 0) {
    const containerRect = this.container.getBoundingClientRect();

    for (const [edgeId, element] of this.labelElements) {
      const rect = element.getBoundingClientRect();
      const relativeX = rx - (rect.left - containerRect.left);
      const relativeY = ry - (rect.top - containerRect.top);

      const expandedWidth = rect.width + 2 * extraPadPx;
      const expandedHeight = rect.height + 2 * extraPadPx;

      if (relativeX >= -extraPadPx &&
        relativeX <= expandedWidth - extraPadPx &&
        relativeY >= -extraPadPx &&
        relativeY <= expandedHeight - extraPadPx) {

        const edge = this.cy.getElementById(edgeId);
        return this.getCustomLabelDataForEdge(edge);
      }
    }

    return null;
  }

  // Project point to edge parameter s [-1,1]
  projectPointToS(edge, mx, my) {
    if (!edge || edge.length === 0) return 0;

    // Sample points along edge to find closest
    const samples = 60;
    let bestT = 0;
    let bestDistance = Infinity;

    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const point = pointOnEdge(edge, t);
      if (point) {
        const dx = point.x - mx;
        const dy = point.y - my;
        const distance = dx * dx + dy * dy;

        if (distance < bestDistance) {
          bestDistance = distance;
          bestT = t;
        }
      }
    }

    // Refine with binary search
    let t = bestT;
    let step = 1 / samples;

    for (let iteration = 0; iteration < 3; iteration++) {
      const t0 = Math.max(0, t - step);
      const t1 = Math.min(1, t + step);

      const p0 = pointOnEdge(edge, t0);
      const p1 = pointOnEdge(edge, t1);

      if (p0 && p1) {
        const d0 = (p0.x - mx) ** 2 + (p0.y - my) ** 2;
        const d1 = (p1.x - mx) ** 2 + (p1.y - my) ** 2;

        t = d0 <= d1 ? t0 : t1;
      }

      step *= 0.5;
    }

    // Convert t [0,1] back to s [-1,1]
    return t * 2 - 1;
  }

  // Hide labels connected to a specific node during drag
  hideConnectedLabels(node) {
    if (!node || !this.labelElements) return;

    const connectedEdges = node.connectedEdges();
    connectedEdges.forEach(edge => {
      const labelElement = this.labelElements.get(edge.id());
      if (labelElement) {
        labelElement.style.visibility = 'hidden';
      }
    });
  }

  // Show labels connected to a specific node after drag
  showConnectedLabels(node) {
    if (!node || !this.labelElements) return;

    const connectedEdges = node.connectedEdges();
    const edgesToUpdate = [];

    connectedEdges.forEach(edge => {
      const labelElement = this.labelElements.get(edge.id());
      if (labelElement) {
        labelElement.style.visibility = 'visible';
        edgesToUpdate.push(edge);
      }
    });

    // Update positions after showing to ensure accuracy
    if (edgesToUpdate.length > 0) {
      this.updateLabelPositions(edgesToUpdate);
    }
  }

  destroy() {
    if (this.isDestroyed) return;

    this.isDestroyed = true;

    // Clear any pending timers
    if (this._parallelEdgeTimeout) {
      clearTimeout(this._parallelEdgeTimeout);
      this._parallelEdgeTimeout = null;
    }
    this.cancelPendingGeometryFlush();

    // Remove event listeners
    this.cy.off('zoom pan', this._positionUpdateHandler);
    this.cy.off('data', 'edge');
    this.cy.off('add', 'edge');
    this.cy.off('remove', 'edge');
    this.cy.off('class', 'edge', this._classHandler);
    this.cy.off('style', 'node', this._nodeGeometryHandler);
    this.cy.off('select unselect', 'node edge', this._delayedRedrawHandler);
    this.cy.off('edgeediting_dragstart', this._startEdgeEditingHandler);
    this.cy.off('edgeediting_moveend', this._endEdgeEditingHandler);
    this.cy.off('edgeediting_anchordrag', this._anchorDragHandler);
    this.cy.off('grab', 'node');
    this.cy.off('drag', 'node');
    this.cy.off('free', 'node');
    window.removeEventListener('resize', this._redrawHandler);

    // Clear labels
    this.clearLabels();

    // Clear all caches
    this.textCache.clear();
    this.classCache.clear();
    this.dataHashCache.clear();
    this.nodeGeometryCache.clear();
    this._pendingGeometryNodeIds = null;

    // Remove container
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    this.container = null;
    this.labelElements = null;
    this.sharedSvg = null;
    this.svgDefs = null;
  }
}

// Factory function for compatibility with existing code
export default function installHtmlEdgeLabels(cy, options = {}) {
  return new HtmlEdgeLabelsRenderer(cy, options);
}
